import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';

import { needsAuth } from '../utils/authHoc';
import { loadChildren } from '../../actions/auth';

import EditProfile from './EditProfile';
import ChangePassword from './ChangePassword';
import { addError, isSubsValid } from '../../actions/utils';
import { getTimeZone } from '@utils/date';
import EditChildModal from '@components/profile/children/EditChildModal';
import AddChildModal from '@components/profile/children/AddChildModal';
import LinkChildModal from '@components/profile/children/LinkChildModal';
import './Settings.scss';
import './settings-rts.scss';
import { userApi } from '@api/user';
import { I18n } from 'react-redux-i18n';
import pick from 'lodash/pick';

const FieldItem = ({ label, value }) => (
  <div className="settings-page--rts__field">
    <div className="settings-page--rts__field-label">{label}</div>
    <div className="settings-page--rts__field-value">{value || '—'}</div>
  </div>
);

class Settings extends React.PureComponent {
  constructor(...args) {
    super(...args);

    this.toggleEditProfile = this.toggleEditProfile.bind(this);
    this.toggleEditPassword = this.toggleEditPassword.bind(this);
    this.toggleAddChild = this.toggleAddChild.bind(this);
    this.toggleLinkChild = this.toggleLinkChild.bind(this);
    this.hideEditChild = this.hideEditChild.bind(this);
    this.onCreateChild = this.onCreateChild.bind(this);
    this.onEditChild = this.onEditChild.bind(this);
    this.onChangeChildPassword = this.onChangeChildPassword.bind(this);
    this.onLinkChild = this.onLinkChild.bind(this);
    this.onDeleteChild = this.onDeleteChild.bind(this);

    this.state = {
      showEditProfile: false,
      showEditPassword: false,
      showAddChild: false,
      showLinkChild: false,
      editChild: undefined,
    };
  }

  toggleEditProfile() {
    this.setState({ showEditProfile: !this.state.showEditProfile });
  }

  toggleEditPassword() {
    this.setState({ showEditPassword: !this.state.showEditPassword });
  }

  toggleAddChild() {
    this.setState({ showAddChild: !this.state.showAddChild });
  }

  toggleLinkChild() {
    this.setState({ showLinkChild: !this.state.showLinkChild });
  }

  hideEditChild() {
    this.setState({ editChild: undefined });
  }

  async onCreateChild(options) {
    const { dispatch } = this.props;
    try {
      await userApi.createChild(options);
      this.setState({ showAddChild: false });
      dispatch(loadChildren());
    } catch (error) {
      dispatch(addError(error.response.body.error));
    }
  }

  async onLinkChild({ name, password }) {
    const { dispatch } = this.props;
    try {
      await userApi.linkChild(name, password);
      this.setState({ showLinkChild: false });
      dispatch(loadChildren());
    } catch (error) {
      dispatch(addError(error.response.body.error));
    }
  }

  async onEditChild(data) {
    const { dispatch } = this.props;
    try {
      const { editChild } = this.state;
      if (editChild) {
        await userApi.updateChild(
          editChild._id,
          pick(data, ['name', 'realname', 'age'])
        );
        dispatch(loadChildren());
      }
      this.setState({ editChild: undefined });
      dispatch(loadChildren());
    } catch (error) {
      dispatch(addError(error.response.body.error));
    }
  }

  async onChangeChildPassword({ _id, password }) {
    const { dispatch } = this.props;
    try {
      await userApi.updateChildPassword(_id, password);
      this.setState({ editChild: undefined });
    } catch (error) {
      dispatch(addError(error.response.body.error));
    }
  }

  async onDeleteChild() {
    const { dispatch } = this.props;
    try {
      const { editChild } = this.state;
      if (editChild) {
        await userApi.deleteChild(editChild._id);
        dispatch(loadChildren());
      }
    } catch (error) {
      dispatch(addError(error));
    }
    this.setState({ editChild: undefined });
  }

  componentDidMount() {
    userApi.getAccessToken();
    this.props.dispatch(loadChildren());
  }

  render() {
    const { user } = this.props;

    if (!user) return null;

    return (
      <div className="settings-page settings-page--rts">
        <div className="settings-page--rts__card">
          <h1 className="settings-page--rts__title">Настройки аккаунта</h1>

          <div className="settings-page--rts__fields">
            <FieldItem label="Имя:" value={user.realname} />
            <FieldItem label="Игровой ник:" value={user.name} />
            <FieldItem label="Почта:" value={user.email} />
            <FieldItem label="Telegram ID:" value={user.telegramId} />
            <FieldItem
              label="Часовой пояс:"
              value={user.timezone || getTimeZone()}
            />
            <FieldItem label="Токен доступа:" value={user.accessToken} />
          </div>

          <div className="settings-page--rts__actions">
            <button
              type="button"
              className="settings-page--rts__action-btn"
              onClick={this.toggleEditProfile}
            >
              {I18n.t('settings.personal.game.edit')}
            </button>
            <button
              type="button"
              className="settings-page--rts__action-btn settings-page--rts__action-btn--secondary"
              onClick={this.toggleEditPassword}
            >
              {I18n.t('settings.personal.password.change')}
            </button>
          </div>
        </div>

        {user.userType === 'parent' && (
          <div className="settings-page--rts__card">
            <h2 className="settings-page--rts__section-title">
              {I18n.t('settings.personal.children.childAccounts')}
            </h2>

            {user.children.length === 0 ? (
              <p className="settings-page--rts__children-empty">Список пуст</p>
            ) : (
              <table className="settings-page--rts__children-table">
                <thead>
                  <tr>
                    <th />
                    <th>Ник</th>
                    <th>Имя</th>
                    <th>Возраст</th>
                  </tr>
                </thead>
                <tbody>
                  {user.children.map(child => (
                    <tr key={child._id}>
                      <td>
                        <button
                          type="button"
                          className="settings-page--rts__action-btn"
                          onClick={() => this.setState({ editChild: child })}
                        >
                          {I18n.t('settings.personal.game.edit')}
                        </button>
                      </td>
                      <td>{child.name}</td>
                      <td>
                        {child.realname ||
                          I18n.t('settings.personal.children.noName')}
                      </td>
                      <td>{child.age}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="settings-page--rts__actions">
              <button
                type="button"
                className="settings-page--rts__action-btn"
                onClick={this.toggleAddChild}
              >
                {I18n.t('settings.personal.children.createAccount')}
              </button>
              <button
                type="button"
                className="settings-page--rts__action-btn settings-page--rts__action-btn--secondary"
                onClick={this.toggleLinkChild}
              >
                {I18n.t('settings.personal.children.linkAccount')}
              </button>
            </div>
          </div>
        )}

        <EditProfile
          show={this.state.showEditProfile}
          close={this.toggleEditProfile}
        />

        <ChangePassword
          show={this.state.showEditPassword}
          close={this.toggleEditPassword}
        />

        <EditChildModal
          child={this.state.editChild}
          show={!!this.state.editChild}
          close={this.hideEditChild}
          onChangeInfo={this.onEditChild}
          onChangePassword={this.onChangeChildPassword}
          onDelete={this.onDeleteChild}
        />

        <AddChildModal
          show={this.state.showAddChild}
          close={this.toggleAddChild}
          onSave={this.onCreateChild}
        />

        <LinkChildModal
          show={this.state.showLinkChild}
          close={this.toggleLinkChild}
          onSave={this.onLinkChild}
        />
      </div>
    );
  }
}

Settings.propTypes = {
  dispatch: PropTypes.func.isRequired,
  user: PropTypes.any.isRequired,
  courses: PropTypes.array.isRequired,
  isSubValid: PropTypes.bool.isRequired,
  available: PropTypes.array.isRequired,
};

function mapStateToProps(state) {
  const { items: courses } = state.courses;
  const { user } = state.auth;
  const { avatars } = state.utils;

  let complete = [],
    available = [],
    isSubValid = false;

  if (user) {
    isSubValid = isSubsValid(user);
    complete = user.stats.completeCourses;
    available = courses
      .filter(
        c =>
          c.unlockCondition.every(el => complete.indexOf(el) !== -1) &&
          (isSubValid || c.tier === 0)
      )
      .map(c => c._id);
  }

  return {
    user,
    courses,
    available,
    isSubValid,
    avatars,
  };
}

export default compose(needsAuth, connect(mapStateToProps))(Settings);
